import React from 'react';
import { ArrowLeft, Checkmark, Close, DocumentPdf } from '@carbon/icons-react';
import { Button, Tag } from '@ditioas/web-ui';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import LocalLanguageSelect, {
  useAnonymousLanguage,
} from 'components/LocalLanguageSelect/LocalLanguageSelect';
import SaveOverlay from 'components/SaveOverlay/SaveOverlay';
import config from 'config/site.config';
import {
  getPublicAlertPdfUrl,
  useGetAlertById,
  useGetPublicAlertById,
} from 'data/alerts';
import useCurrentUserAndSettings from 'hooks/useCurrentUserAndSettings';
import useObjectSearchParams from 'hooks/useObjectSearchParams';
import useShowAlert from 'hooks/useShowAlert';
import { getPublic } from 'libs/apiClient';
import { isLoadingOidcUserAtom } from 'libs/authState';
import cn from 'libs/cn';
import { parseAndFormatDate } from 'libs/dateFunctions';
import { getFileUrl } from 'libs/urlFunctions';
import type { AlertCustomProperty } from 'model/Alerts/types';
import { CompanyAlertBaseType, InjuryType } from 'model/Alerts/types';

import { getAlertStatusColor } from '../Alert/utils';
import { DitioMark } from './DitioMark';
import InlineInfo from './InlineInfo/InlineInfo';

const {
  mapbox: { accessToken },
  modules: { alerts: alertsUrls },
} = config;

/* Mobile-first: rows stack on phones (email-linked anonymous view) and become a
   horizontal band on larger screens — and always when printing. */
const rowClassName =
  'flex flex-col gap-4 sm:flex-row sm:justify-between print:flex-row print:justify-between print:pt-0';

const sectionClassName = 'mt-6 flex flex-col gap-2 break-inside-avoid';

/* Bordered label | value table (matches the branded alert PDF). */
const infoTableClassName =
  'border-sand-800 divide-sand-800 w-full divide-y overflow-hidden rounded-md border break-inside-avoid';

const detailContainerClassName = 'flex items-center gap-x-2 break-inside-avoid';

const getCustomPropertyValue = (property: AlertCustomProperty) => {
  if (property.textValue) {
    return property.textValue;
  }
  if (property.datasetItemName) {
    return property.datasetItemNumber
      ? `${property.datasetItemName} - ${property.datasetItemNumber}`
      : property.datasetItemName;
  }

  return property.dropdownOptions?.map(({ name }) => name).join(', ');
};

// Strip path-hostile / filesystem-reserved characters and collapse whitespace
// so the download name is safe across Windows/macOS/Linux.
const sanitizeFileNamePart = (value: string): string =>
  value
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

// The server may already send a nice filename via Content-Disposition; honour it
// (RFC 5987 filename*= takes precedence over the plain filename=) before falling
// back to a client-built name.
const getFileNameFromContentDisposition = (
  disposition: string | null
): string | undefined => {
  if (!disposition) return undefined;

  const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(
        utf8Match[1].trim().replace(/^["']|["']$/g, '')
      );
    } catch {
      // Malformed encoding — fall through to the plain filename below.
    }
  }

  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);

  return plainMatch?.[1]?.trim() || undefined;
};

const ReadOnlyAlert = () => {
  // The report renders in its own language so an anonymous reader can switch it
  // without touching the app-wide one (see useAnonymousLanguage).
  const [language, setLanguage] = useAnonymousLanguage();

  const { t } = useTranslation(undefined, {
    lng: language,
  });
  const { currentUser, oidcUser } = useCurrentUserAndSettings();
  const isLoadingOidcUser = useAtomValue(isLoadingOidcUserAtom);
  const { alertId } = useParams<{ alertId?: string }>();
  const { printOnLoad } = useObjectSearchParams<{
    printOnLoad?: boolean;
  }>();

  const { xai, xat } = useObjectSearchParams<{ xai?: string; xat?: string }>();
  const { alert, isFetching: isFetchingAlert } = useGetAlertById(
    alertId,
    !!(xai && xat)
  );
  const { alert: publicAlert, isFetching: isFetchingPublicAlert } =
    useGetPublicAlertById(alertId, xai, xat);
  const alertToView = React.useMemo(
    () => (!xai && !xat ? alert : publicAlert),
    [xai, xat, alert, publicAlert]
  );
  const isFetching = React.useMemo(
    () => (!xai && !xat ? isFetchingAlert : isFetchingPublicAlert),
    [xai, xat, alert, publicAlert]
  );

  const canDownloadPdf = !!(alertId && xai && xat);
  // Anonymous viewers reach this page from an email link, outside the app
  // Layout — so there is no TopBar language selector to fall back on.
  //
  // Both flags have to settle first: `oidcUser` is null while the session is
  // still being read from storage, and `currentUser` is undefined while a
  // signed-in user's profile is in flight. Testing either one alone shows the
  // selector to signed-in users for a frame and then takes it away again.
  const showLanguageSelect = !isLoadingOidcUser && !oidcUser;

  const [imagesToLoad, setImagesToLoad] = React.useState<null | number>(null);
  const { showErrorMessage } = useShowAlert();
  const [isDownloadingPdf, setIsDownloadingPdf] = React.useState(false);

  // Branded, timestamped download name mirroring the backend PDF naming
  // (Ditio_<Type>_<Serial>_<yyyy-MM-dd>.pdf), e.g. Ditio_Avvik_T0035_2026-07-02.pdf.
  const buildPdfFileName = (): string => {
    const typeName = alertToView?.typeName || 'Varsel';
    const serial =
      alertToView?.serialNumberWithPrefix ||
      `${alertToView?.serialNumberPrefix ?? ''}${alertToView?.serialNumber ?? alertId ?? ''}`;
    const dateSuffix = parseAndFormatDate({
      value: alertToView?.createdAt ?? '',
      format: 'yyyy-MM-dd',
    });

    const parts = ['Ditio', typeName, serial, dateSuffix]
      .map((part) => sanitizeFileNamePart(String(part)))
      .filter(Boolean);

    return `${parts.join('_')}.pdf`;
  };

  // Tokens minted before the PDF endpoint shipped (timestyr#3445) 401 on the
  // /pdf sibling while the read view keeps working, so download the blob and
  // surface failures as a toast instead of opening a raw error page.
  const handleDownloadPdf = async (): Promise<void> => {
    if (!alertId || !xai || !xat) return;

    setIsDownloadingPdf(true);
    try {
      const response = await getPublic(getPublicAlertPdfUrl(alertId, xai, xat));
      const pdfBlob = await response.blob();
      const objectUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download =
        getFileNameFromContentDisposition(
          response.headers.get('content-disposition')
        ) ?? buildPdfFileName();
      document.body.appendChild(link);
      link.click();
      link.remove();
      // iOS Safari aborts downloads whose blob URL is revoked before the save
      // actually starts, so defer the revoke instead of doing it synchronously.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      showErrorMessage({ message: t('General.ErrorDescription') });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const assignees =
    alertToView?.assignedToUsers ??
    (alertToView?.assignedTo ? [alertToView.assignedTo] : []);

  React.useEffect(() => {
    if (printOnLoad && !isFetching) {
      if (!imagesToLoad) {
        const imageElements = Array.from(document.getElementsByTagName('img'));
        setImagesToLoad(imageElements.length);
      }

      if (imagesToLoad === 0) {
        window.print();
      }
    }
  }, [printOnLoad, isFetching, imagesToLoad]);

  return isFetching || !alertToView ? (
    <SaveOverlay />
  ) : (
    /* Mobile-first: tight gutters for phones. The print-color-adjust pair keeps
       grey label cells and status colors from being dropped in print/PDF export. */
    <div className="relative mx-auto w-full max-w-250 overflow-auto bg-white p-4 [-webkit-print-color-adjust:exact] [print-color-adjust:exact] sm:my-8 sm:p-8 print:my-0 print:h-fit print:max-w-none print:overflow-y-visible print:p-0">
      <div className="flex flex-col">
        <div className={cn(rowClassName, 'print:hidden')}>
          {currentUser && (
            <Button
              asChild
              variant="outline"
              leftIcon={<ArrowLeft size={24} />}
              className="mb-auto shrink-0"
            >
              <Link
                to={`${alertsUrls.listUrl}/${alertToView.id}?projectId=${alertToView.projectId}&projectName=${alertToView.projectName}`}
              >
                {t('Alerts.ReadOnly.ToAlertPage')}
              </Link>
            </Button>
          )}
          {(showLanguageSelect || canDownloadPdf) && (
            /* Stacked on phones, right-aligned actions on larger screens */
            <div className="flex w-full flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-end">
              {showLanguageSelect && (
                <LocalLanguageSelect value={language} onChange={setLanguage} />
              )}
              {canDownloadPdf && (
                <Button
                  variant="primary"
                  leftIcon={<DocumentPdf size={20} />}
                  loading={isDownloadingPdf}
                  onClick={handleDownloadPdf}
                  className="w-full sm:w-auto"
                >
                  {t('PdfGeneration.DownloadPDFs', { count: 1 })}
                </Button>
              )}
            </div>
          )}
        </div>
        <div className={cn(rowClassName, 'pt-4')}>
          <div>
            <h1 className="heading-md mb-1">
              {`${
                alertToView.motherCompanyName
                  ? `${alertToView.company.name} / `
                  : ''
              }${
                alertToView.projectNumber?.length > 0
                  ? `${alertToView.projectNumber}  / `
                  : ''
              }${alertToView.projectName}`}
            </h1>
            {alertToView.taskName && (
              <div className="body-sm text-gray">{alertToView.taskName}</div>
            )}
          </div>
          {/* Mobile-first: stacks under the project heading, left-aligned */}
          <div className="flex flex-col items-start gap-1 text-left sm:ml-auto sm:items-end sm:text-right">
            <img
              className="mb-1 max-h-28 w-full max-w-32 object-contain"
              src={getFileUrl(alertToView.company.logoImageFileRef144, 144)}
              alt={alertToView.company.name}
              onLoad={() =>
                setImagesToLoad((toLoad) =>
                  typeof toLoad === 'number' ? toLoad - 1 : 0
                )
              }
            />
            <div className="heading-md">
              {alertToView.motherCompanyName || alertToView.company.name}
            </div>
          </div>
        </div>
        <div className={rowClassName}>
          <header className="flex flex-col gap-3">
            <div className="heading-sm text-gray mb-0.5">
              {alertToView.typeName}
            </div>
            <h2 className="heading-lg">
              {`${alertToView.serialNumberWithPrefix} - ${alertToView.title}`}
            </h2>
            <Tag
              indicator
              size="sm"
              color={getAlertStatusColor(alertToView.status)}
              text={t(
                `Alerts.AlertStatus.${alertToView.status}` as 'Alerts.AlertStatus.0'
              )}
            />
          </header>
        </div>

        <section className={cn(sectionClassName, 'mt-4')}>
          <div className={infoTableClassName}>
            <InlineInfo
              label={t('General.SerialNumber')}
              value={alertToView.projectSerialNumberFull}
            />
            {alertToView.machineName && (
              <InlineInfo
                label={t('Title.Machine')}
                value={
                  alertToView.machineResourceNumber
                    ? `${alertToView.machineResourceNumber} - ${alertToView.machineName}${alertToView.machineSerialNumber ? ` / ${alertToView.machineSerialNumber}` : ''}`
                    : `${alertToView.machineName}${alertToView.machineSerialNumber ? ` / ${alertToView.machineSerialNumber}` : ''}`
                }
              />
            )}
            <InlineInfo
              label={t('Alerts.ReadOnly.SubmittedAt')}
              value={parseAndFormatDate({
                value: alertToView.createdAt,
                format: 'dd.MM.yyy - HH:mm',
              })}
            />
            <InlineInfo
              label={t('General.By')}
              value={`${alertToView.createdBy.name} / ${alertToView.createdBy.mobilePhone} / ${alertToView.createdBy.companyName}`}
            />
            <InlineInfo
              label={t('Alerts.ReadOnly.Category')}
              value={alertToView.categories.map(({ name }) => name).join(', ')}
              optional
            />
            <InlineInfo
              label={t('Alerts.ReadOnly.AssignedTo')}
              value={
                assignees.length
                  ? assignees
                      .map(
                        (user) =>
                          `${user.name} / ${user.mobilePhone} / ${user.companyName}`
                      )
                      .join('\n')
                  : t('General.NotApplicable')
              }
            />
            <InlineInfo
              label={t('Alerts.ReadOnly.ExternalId')}
              value={alertToView.externalId}
              optional
            />
            {!!alertToView.longitude && !!alertToView.latitude && (
              <InlineInfo
                label={t('Alerts.ReadOnly.Coordinates')}
                value={`${alertToView.latitude},${alertToView.longitude}`}
              />
            )}
          </div>
        </section>
        <section className={sectionClassName}>
          <div className={infoTableClassName}>
            <InlineInfo
              label={t('Alerts.ReadOnly.CauseOrDescription')}
              value={alertToView.description}
            />
            <InlineInfo
              label={t('Alerts.ReadOnly.MeasuresTakenOrSuggestions')}
              value={alertToView.measures}
              optional
            />
          </div>
          <div className={detailContainerClassName}>
            <span className="body-sm">{t('Alerts.ReadOnly.FixedOnSpot')}:</span>
            {alertToView.resolvedOnLocation ? (
              <Checkmark size={24} className="text-success" />
            ) : (
              <Close size={24} className="text-error" />
            )}
            <span className="body-sm">
              {t('Alerts.ReadOnly.NeedsFollowUp')}:
            </span>
            {alertToView.requiresFurtherAction ? (
              <Checkmark size={24} className="text-success" />
            ) : (
              <Close size={24} className="text-error" />
            )}
          </div>
        </section>

        {alertToView.commentInternal && (
          <section className={sectionClassName}>
            <div className={infoTableClassName}>
              <InlineInfo
                label={t('Alerts.ReadOnly.InternalComment')}
                value={alertToView.commentInternal}
                optional
              />
            </div>
          </section>
        )}

        {alertToView.imageIds.length > 0 && (
          <section className={sectionClassName}>
            <h2 className="heading-md mb-2">{t('General.Images')}</h2>
            <div
              className={cn(
                detailContainerClassName,
                'flex-col items-start gap-x-0 gap-y-1'
              )}
            >
              {/* Mobile-first: single column, two-up on wider screens */}
              <div className="xs:grid-cols-2 grid grid-cols-1 gap-2 print:grid-cols-2">
                {alertToView.imageIds.map((imageId, index) => {
                  const coords = [
                    alertToView.imageFileReferences[index]?.geoCoordinate
                      ?.latitude,
                    alertToView.imageFileReferences[index]?.geoCoordinate
                      ?.longitude,
                  ];

                  return (
                    <div className="flex flex-col" key={imageId}>
                      <img
                        className="mx-auto mb-auto max-h-150 max-w-full rounded-sm"
                        alt={imageId}
                        src={getFileUrl(imageId, 1200)}
                        onLoad={() =>
                          setImagesToLoad((toLoad) =>
                            typeof toLoad === 'number' ? toLoad - 1 : 0
                          )
                        }
                      />
                      {!!coords[0] && !!coords[1] && (
                        <span className="body-sm text-brand mr-auto">
                          {coords.join(', ')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {alertToView.fileIds.length > 0 && (
          <section className={sectionClassName}>
            <h2 className="heading-md mb-2">{t('General.Attachments')}</h2>
            <div
              className={cn(
                detailContainerClassName,
                'flex-col items-start gap-x-0 gap-y-1'
              )}
            >
              {alertToView.fileFileReferences.map((file) => (
                <Link
                  key={file.id}
                  to={getFileUrl(file.id, 0)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="body-sm text-brand hover:underline">
                    {file.fileNameOrig}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className={sectionClassName}>
          <h2 className="heading-md mb-2">
            {t('Alerts.Report.AdditionalInfo')}
          </h2>
          <div className={infoTableClassName}>
            {alertToView.showRisk && (
              <InlineInfo
                label={t('Alerts.ReadOnly.SeverityLevel')}
                value={t(`Alerts.Report.SeverityLevels.${alertToView.risk}`)}
              />
            )}
            {alertToView.relatedTo && (
              <InlineInfo
                label={t('Alerts.ReadOnly.WorkConnectedWith')}
                value={alertToView.relatedTo.map(({ name }) => name).join(', ')}
                optional
              />
            )}
            {alertToView.causes && (
              <InlineInfo
                label={t('Alerts.ReadOnly.Cause')}
                value={alertToView.causes.map(({ name }) => name).join(', ')}
                optional
              />
            )}
            {alertToView.involvedParties && (
              <InlineInfo
                label={t('Alerts.ReadOnly.InvolvedParties')}
                value={alertToView.involvedParties
                  .map(({ name }) => name)
                  .join(', ')}
                optional
              />
            )}
            {!!alertToView.customProperties?.length &&
              alertToView.customProperties.map((property) =>
                getCustomPropertyValue(property) ? (
                  <InlineInfo
                    key={property.propertyId}
                    label={property.propertyName}
                    value={getCustomPropertyValue(property)}
                    optional
                  />
                ) : null
              )}
            <InlineInfo
              label={t('Alerts.ReadOnly.InvolvedResources')}
              value={alertToView.involvedResources}
              optional
            />
            <InlineInfo
              label={t('Alerts.ReadOnly.InvolvedCompanies')}
              value={alertToView.involvedCompanies
                ?.map((company) => company.name)
                .join(', ')}
              optional
            />

            {alertToView.baseType !== CompanyAlertBaseType.Other &&
              alertToView.showDamageInfo && (
                <>
                  <InlineInfo
                    label={t(
                      `Alerts.ReadOnly.DamageLabel.${alertToView.baseType}` as 'Alerts.ReadOnly.DamageLabel.0'
                    )}
                    value={t(
                      `Alerts.Report.DamageTypes.${alertToView.baseType}.${alertToView.damageType}` as 'Alerts.Report.DamageTypes.0.0'
                    )}
                  />
                  {alertToView.baseType === CompanyAlertBaseType.HSE &&
                    alertToView.damageType === InjuryType.WithAbsence && (
                      <InlineInfo
                        label={t('Alerts.ReadOnly.NumOfDaysOfAbsence')}
                        value={alertToView.absenceDays}
                      />
                    )}
                </>
              )}
            {alertToView.showCost && (
              <>
                <InlineInfo
                  label={t('Alerts.ReadOnly.EstimatedCost')}
                  value={alertToView.costs ?? '-'}
                />
                <InlineInfo
                  label={t('Alerts.ReadOnly.InvoicedCost')}
                  value={alertToView.invoicedCost ?? '-'}
                />
                <InlineInfo
                  label={t('Alerts.ReadOnly.InternalCost')}
                  value={alertToView.internalCost ?? '-'}
                />
              </>
            )}
          </div>
        </section>
        <section className={sectionClassName}>
          <span className="label-sm mr-auto">{t('Title.Location')}:</span>
          {alertToView.latitude && alertToView.longitude ? (
            /* Mobile-first: map stacks above the details; side by side on wider screens */
            <div className="flex break-inside-avoid flex-col items-start gap-x-6 gap-y-3 sm:flex-row print:flex-row">
              <img
                className="aspect-square h-auto w-full max-w-84 rounded-sm"
                alt={`lat:${alertToView.latitude}_long:${alertToView.longitude}`}
                src={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s(${alertToView.longitude},${alertToView.latitude})/${alertToView.longitude},${alertToView.latitude},13,0/450x450?access_token=${accessToken}`}
                onLoad={() =>
                  setImagesToLoad((toLoad) =>
                    typeof toLoad === 'number' ? toLoad - 1 : 0
                  )
                }
              />
              <div className="flex flex-col gap-1">
                <span className="body-sm">
                  {t('Alerts.ReadOnly.Coordinates')}
                </span>
                <a
                  className="body-xs text-brand hover:underline"
                  href={`https://www.google.com/maps?q=${alertToView.latitude},${alertToView.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${alertToView.latitude}, ${alertToView.longitude}`}
                </a>
              </div>
            </div>
          ) : (
            <span className="body-sm">
              {t('Alerts.ReadOnly.InvalidLocation')}
            </span>
          )}
        </section>

        {/* Footer mark (matches the PDF "Generert av Ditio · ditio.no" footer) */}
        <footer className="border-sand-800 mt-10 flex break-inside-avoid flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t pt-4">
          <DitioMark className="h-5 w-auto opacity-55" />
          <span className="body-xxs text-gray">
            {t('Alerts.ReadOnly.GeneratedByDitio')}
          </span>
        </footer>
      </div>
    </div>
  );
};

export default ReadOnlyAlert;
